import { useMemo } from 'react';

import { useBehaviorController_getRespiration } from '@/services/hooks';
import type { RespirationResponseDto } from '@/services/types';

import type { DomainQueryOptions } from '../shared/types';

import { mapRespirationResponseToDomain } from './mappers';
import { toBehaviorRangeQuery } from './queryParams';
import type { BehaviorDateRangeParams, UseGetRespirationResult } from './types';

const EMPTY_RESPIRATION_DATA: RespirationResponseDto = {
    data: [],
    lastMeasurement: '',
};

/**
 * Domain hook to fetch respiration data for a cage
 * Returns respiration data points including active/passive percentages
 * and swim speed context for the specified date range
 *
 * @param cageId - The cage ID to fetch respiration data for
 * @param params - Optional date range parameters (from/to in YYYY-MM-DD format)
 * @returns Query result with mapped respiration data
 */
export const useGetRespiration = (
    cageId: number,
    params?: BehaviorDateRangeParams,
    options?: DomainQueryOptions<RespirationResponseDto>
): UseGetRespirationResult => {
    const respirationQuery = useBehaviorController_getRespiration(
        cageId,
        toBehaviorRangeQuery(params),
        options
    );

    const respirationData = useMemo(
        () => mapRespirationResponseToDomain(respirationQuery.data?.data ?? EMPTY_RESPIRATION_DATA),
        [respirationQuery.data?.data]
    );

    return {
        ...respirationQuery,
        isLoading: respirationQuery.isPending,
        data: respirationData,
    };
};
